import React from "react";

/**
 * Editor for the people in an investor household → persisted to investor_members.
 * Controlled: `value` is an array of member objects, `onChange(newArray)` writes back.
 *
 * Member shape: { first_name, last_name, email, phone, is_primary, us_person, us_status }
 * Exactly one member is primary (radio). Fully-empty rows are ignored on save (server-side).
 */

const US_PERSON_OPTS = ["Yes", "No"];
const US_STATUS_OPTS = ["Citizen", "Green Card", "Visa", "Other"];

export const emptyMember = (isPrimary = false) => ({
  first_name: "", last_name: "", email: "", phone: "", is_primary: isPrimary, us_person: "", us_status: "",
});

const cellInput = {
  width: "100%", padding: "8px 9px", border: "1.5px solid #dfe3e8", borderRadius: "6px",
  fontSize: "13.5px", boxSizing: "border-box", backgroundColor: "white",
};
const cellSelect = { ...cellInput, cursor: "pointer" };

export default function MembersEditor({ value, onChange, contactEmail = "", contactPhone = "" }) {
  const rows = Array.isArray(value) && value.length ? value : [emptyMember(true)];

  const update = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const setPrimary = (i) => onChange(rows.map((r, idx) => ({ ...r, is_primary: idx === i })));
  const add = () => onChange([...rows, emptyMember(rows.length === 0)]);
  const remove = (i) => {
    const next = rows.filter((_, idx) => idx !== i);
    // Keep a primary designated.
    if (next.length && !next.some((r) => r.is_primary)) next[0] = { ...next[0], is_primary: true };
    onChange(next.length ? next : [emptyMember(true)]);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {rows.map((m, i) => (
        <div key={i} style={{
          border: "1px solid #e6e9ee", borderRadius: "10px", padding: "12px 14px",
          backgroundColor: m.is_primary ? "#f4f9fc" : "#fafbfc",
        }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
            <label style={{ display: "flex", alignItems: "center", gap: "7px", fontSize: "12.5px", fontWeight: 600, color: m.is_primary ? "#1565c0" : "#5f6b78", cursor: "pointer" }}>
              <input type="radio" name="member-primary" checked={!!m.is_primary} onChange={() => setPrimary(i)} />
              {m.is_primary ? "Primary contact" : "Set as primary"}
            </label>
            {rows.length > 1 && (
              <button type="button" onClick={() => remove(i)} aria-label="Remove member"
                style={{ border: "none", background: "none", color: "#a5281b", cursor: "pointer", fontSize: "13px", fontWeight: 600 }}>
                Remove
              </button>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "10px" }}>
            <input style={cellInput} placeholder="First name" value={m.first_name || ""} onChange={(e) => update(i, { first_name: e.target.value })} />
            <input style={cellInput} placeholder="Last name" value={m.last_name || ""} onChange={(e) => update(i, { last_name: e.target.value })} />
            <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
              <input style={cellInput} placeholder="Email" type="email" value={m.email || ""} onChange={(e) => update(i, { email: e.target.value })} />
              {contactEmail && m.email !== contactEmail && (
                <button type="button" onClick={() => update(i, { email: contactEmail })}
                  style={{ alignSelf: "flex-start", border: "none", background: "none", color: "#1976d2", cursor: "pointer", fontSize: "11px", fontWeight: 600, padding: "1px 0" }}>
                  ↓ Use contact email
                </button>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "3px" }}>
              <input style={cellInput} placeholder="Phone" type="tel" value={m.phone || ""} onChange={(e) => update(i, { phone: e.target.value })} />
              {contactPhone && m.phone !== contactPhone && (
                <button type="button" onClick={() => update(i, { phone: contactPhone })}
                  style={{ alignSelf: "flex-start", border: "none", background: "none", color: "#1976d2", cursor: "pointer", fontSize: "11px", fontWeight: 600, padding: "1px 0" }}>
                  ↓ Use contact phone
                </button>
              )}
            </div>
            <select style={cellSelect} value={m.us_person || ""} onChange={(e) => update(i, { us_person: e.target.value })}>
              <option value="">US person? —</option>
              {US_PERSON_OPTS.map((o) => <option key={o} value={o}>{`US person: ${o}`}</option>)}
            </select>
            <select style={cellSelect} value={m.us_status || ""} onChange={(e) => update(i, { us_status: e.target.value })}>
              <option value="">US status —</option>
              {US_STATUS_OPTS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
        </div>
      ))}
      <button type="button" onClick={add}
        style={{ alignSelf: "flex-start", padding: "8px 14px", borderRadius: "8px", fontSize: "13px", fontWeight: 600, border: "1.5px dashed #b7c0cc", backgroundColor: "#fff", color: "#3d4753", cursor: "pointer" }}>
        + Add member
      </button>
    </div>
  );
}
